/**
 * `useNoteQuery` — fetch one note + its companion (frontend.md §2.1 `<ResultDetailDrawer>`,
 * `<NodeInspector>`).
 *
 * Used by the Search result detail drawer and the Graph node inspector — anywhere a
 * surface needs the full `NoteView` for a single note. `includeCompanion` defaults true
 * (the drawer shows entities/topics/action-items); a caller wanting a cheap body-only read
 * passes false.
 *
 * The returned `NoteView` is embedding-free by construction (conventions.md §6) — the
 * client's `assertNoEmbedding` guard already ran on the wire payload.
 */

import { useQuery, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { getNote } from '@/api';
import type { NoteId, NoteView } from '@/types';
import { queryKeys } from '@/providers';
import { useErrorHandler } from './useErrorHandler';

/** Note-body stale window — shared by the query + prefetch so they land in the same cache slot. */
const NOTE_STALE_TIME_MS = 5 * 60_000;

/**
 * Warm the cache for a note's body so a subsequent click opens the editor instantly
 * (prefetch-on-hover, Phase 3 perf). Defaults to the editor's cheap body-only read
 * (`includeCompanion: false`) — the sidecar's companion read fills in progressively after
 * the click. No-op if the note is already cached + fresh (prefetchQuery respects staleTime).
 */
export function prefetchNote(
  qc: QueryClient,
  noteId: NoteId,
  includeCompanion = false,
): void {
  void qc.prefetchQuery({
    queryKey: [...queryKeys.note(noteId), includeCompanion],
    queryFn: async () => (await getNote({ id: noteId, includeCompanion })).data,
    staleTime: NOTE_STALE_TIME_MS,
  });
}

export function useNoteQuery(
  noteId: NoteId | null,
  includeCompanion = true,
): UseQueryResult<NoteView, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: [...queryKeys.note(noteId ?? ''), includeCompanion],
    enabled: noteId !== null,
    // Note bodies change only via edits in THIS app, and the save path patches the cache
    // (EditorSurface onSave) — so a 5min stale window safely makes re-opening a recently
    // viewed note instant with no background refetch (Phase 3 perf, 2026-06-10).
    staleTime: NOTE_STALE_TIME_MS,
    queryFn: async () => {
      try {
        return (await getNote({ id: noteId as NoteId, includeCompanion })).data;
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}
